'use strict';

const dates = require('util/date');

const groupBy = require('lodash/groupBy');
const forEach = require('lodash/forEach');
const get = require('lodash/get');
const intersectionWith = require('lodash/intersectionWith');
const pickBy = require('lodash/pickBy');
const merge = require('lodash/merge');
const orderBy = require('lodash/orderBy');
const map = require('lodash/map');
const isString = require('lodash/isString');
const has = require('lodash/has');
const uniq = require('lodash/uniq');
const moment = require('moment');

const ACCESSIBILITY_KEYWORDS = [
    'Captioned',
    'Relaxed Performance',
    'Audio Described',
    'Sign Interpreted Series',
];

const SEAT_COUNTS = {
    3: 740,
    4: 411,
    5: 1022,
    6: 900,
    7: 62,
    15: 1052,
    26: 80,
    57: 1760
};

module.exports = ['$location', '$sce', 'inventoryService', ($location, $sce, inventoryService) => {
    // Store UTM parameters
    let utmParamCache = null;

    // Today's date for querying
    const today = dates.moment();
    const oneMonthsTime = today.clone().add(30, 'days');

    // Set the fuse options for use elsewhere
    const fuseOptions = {
        shouldSort: false,
        threshold: 0.2,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 3,
        keys: [
            { name: 'title', weight: 0.7 },
            { name: 'prefix', weight: 0.3 },
            { name: 'suffix', weight: 0.3 },
            { name: 'facets.keyword', weight: 0.5 }
        ]
    };

    function getPerformances() {
        return inventoryService.getPerformances().then(response => {
            response = response.filter(performance => {
                const inPast = performance.start_date_moment.isBefore(today);

                return !inPast;
            });

            return response.map(performance => {
            
                let min_price = parseFloat(performance.min_price).toFixed(2);
                let min_price_display = min_price.replace('.00', '');
                let max_price = parseFloat(performance.max_price).toFixed(2);
                let max_price_display = max_price.replace('.00', '');

                let price_range_display = min_price_display;
                if (min_price !== max_price ) {
                    price_range_display = price_range_display + '-' + max_price_display.toLocaleString();
                }

                performance.prices_collection = uniq(performance.prices.reduce((acc, price)=>{
                    if (price.available !== 'N') {
                        acc.push(parseFloat(price.price));
                    }
                    return acc;
                },[]));

                performance.min_price = parseFloat(min_price);
                performance.max_price = parseFloat(max_price);
                performance.min_price_display = min_price_display;
                performance.max_price_display = max_price_display;
                performance.price_range_display = price_range_display;

                facetiseAvailability(performance, performance.facets);

                performance.displayTime = performance.start_date_moment.format('h:mma');

                var keywords = get(performance, 'facets', []);
            
                keywords = keywords.map(function (item) {
                    return {
                        category: item.category,
                        keyword: item.keyword,
                    };
                });
                
                var dayTimeKeywords = getDayTimeKeywords(performance.start_date_moment);
                keywords = [].concat(dayTimeKeywords, keywords);
                
                keywords = [].concat([{category: 'location', keyword: performance.facility_title}], keywords);
                
                var accessibilityKeywords = getAccessibilityKeywords(keywords);
                keywords = [].concat(accessibilityKeywords, keywords);
                
                performance.keywords = keywords;            

                performance.is_series = performance.types.some(item => {
                    return item === true;
                });

                return performance;
            });
        });
    }

    function getDayTimeKeywords(date) {
        var categories = [];

        var day = date.format('dddd');

        categories.push({
            category: 'mm_day_search',
            keyword: day
        });

        // Add time
        var hour = date.format('k');
        var time = 'Evening';

        if (hour < 12) {
            time = 'Morning';
        } else if (hour < 18) {
            time = 'Afternoon';
        }

        categories.push({
            category: 'mm_time_search',
            keyword: time
        });

        return categories;
    }

    function getAccessibilityKeywords(keywords) {

        var accessibilityKeywords = [];
        forEach(keywords, function (keyword) {            
            if (ACCESSIBILITY_KEYWORDS.indexOf(keyword.keyword) >= 0) {
                accessibilityKeywords.push({
                    category: 'accessiblity',
                    keyword: keyword.keyword,
                });
            }
        });

        return accessibilityKeywords;
    }

    function getPerformancesByPrices() {
        return inventoryService.getPerformancesByPrices().then(response => {
            response = response.filter(performance => {
                const inPast = performance.start_date_moment.isBefore(today);

                const availablePrices = performance.prices.filter(price => {
                    return get(price, 'available', 'N') === 'Y';
                });

                return !inPast && availablePrices.length;
            });

            return response.map(performance => {
                let facets = [];

                facetiseDays(performance, facets);
                facetiseAvailability(performance, facets);
                facetiseTime(performance, facets);
                facetiseTheatrePlus(performance, facets);
                facetiseOptions(performance, facets);
                facetiseProduction(performance, facets);

                performance.facets = facets;

                performance.price_float = parseFloat(performance.price);
                performance.start_date_value = performance.start_date_moment.valueOf();

                return performance;
            });
        });
    }

    function getPerformancesBySeason() {
        return getPerformancesByPrices().then(response => {
            let seasons = {};

            for(let performance of response) {
                let seasonID = performance.prod_season_no;

                if(!seasons[seasonID]) {
                    seasons[seasonID] = {
                        id: seasonID,
                        title: performance.title,
                        performances: [],
                        availableMonths: [],
                    }
                }

                let monthFacets = generateMonthFacets(performance);

                performance.monthFacets = monthFacets;

                for(let month of monthFacets) {
                    if(seasons[seasonID].availableMonths.indexOf(month) == -1) {
                        seasons[seasonID].availableMonths.push(month);
                    }
                }

                seasons[seasonID].performances.push(performance);
            }

            return Object.keys(seasons).map(x => seasons[x]);
        })
    }

    function generateMonthFacets(performance, facets) {
        let f = [];

        if(performance.start_date_moment.isBefore(oneMonthsTime)) {
            f.push('Next 30 days');

            if(facets) {
                facets.push({
                    category: 'Month',
                    keyword: 'next 30 days',
                    sort: 0
                });
            }
        }

        let monthString = performance.start_date_moment.format('MMMM');

        if(today.year() != performance.start_date_moment.year()) {
            monthString += ' ' + performance.start_date_moment.year()
        }

        f.push(monthString);

        if(facets) {
            facets.push({
                category: 'Month',
                keyword: monthString,
                sort: 0
            });
        }

        return f;
    }

    function facetiseLocation(performance, facets) {

        facets.push({
            category: 'Location',
            keyword: performance.facility_title,
        });

    }

    function facetiseDays(item, facets) {
        const date = item.start_date_moment;

        facets.push({
            category: 'Day',
            keyword: date.format('dddd'),
            sort: date.day()
        });
    }

    function facetiseAvailability(item, facets) {
        if (!has(item, 'facility_no')) {
            return false;
        }

        const facility = item.facility_no;

        if (!has(SEAT_COUNTS, facility)) {
            return false;
        }

        const availability = item.availability;
        const maxAvailability = SEAT_COUNTS[facility];
        const percentageAvailability = Math.round(availability / maxAvailability * 100);

        if (item.is_sold_out) {
            item.flag = 'Sold Out';
        } else if (item.is_on_sale === false || item.prices.length === 0) {
            item.flag = 'Not Available';
        } else if (availability <= 0) {
            if (item.available_on_default) {
                item.flag = 'Not Available';
            } else {
                item.flag = 'Sold Out';
            }
        } else if (percentageAvailability <= 10) {
            facets.push({
                category: 'Availability',
                keyword: 'Limited',
                sort: 2
            });

            item.flag = 'Limited Availability';
        } else if (percentageAvailability <= 50) {
            facets.push({
                category: 'Availability',
                keyword: 'Good',
                sort: 1
            });
        } else {
            facets.push({
                category: 'Availability',
                keyword: 'High',
                sort: 0
            });
        }
    }

    function facetiseTime(item, facets) {
        const time = item.start_date_moment.format('HH');

        if (time > 18) {
            facets.push({
                category: 'Time',
                keyword: 'Evening'
            });

            item.matinee = false;
        } else {
            facets.push({
                category: 'Time',
                keyword: 'Matinee'
            });

            item.matinee = true;
        }
    }

    function facetiseOptions(item, facets) {
        forEach(item.facets, facet => {
            if (facet.keyword === 'Best Seats Available') {
                facets.push({
                    category: 'Options',
                    keyword: facet.keyword,
                    sort: 1
                });
            }
        });
    }

    function facetiseTheatrePlus(item, facets) {
        forEach(item.facets, facet => {
            if (facet.category === 'Theatre-Plus') {
                facets.push({
                    category: 'Theatre-Plus',
                    keyword: facet.keyword,
                    sort: facet.keyword.charAt(0),
                });
            }
        });
    }

    function facetiseAccessibility(item, facets) {
        forEach(item.facets, facet => {
            if (ACCESSIBILITY_KEYWORDS.indexOf(facet.keyword) >= 0) {
                facets.push({
                    category: 'Accessibility',
                    keyword: facet.keyword,
                    sort: facet.keyword.charAt(0),
                });
            }
        });
    }

    function facetiseProduction(item, facets) {
        facets.push({
            category: 'Production',
            keyword: item.title,
        });
    }

    function generateFacets(items) {
        // Keep track of filters
        var filters = [
            {
                category: 'Day',
                title: 'Days',
                keywords: [],
                canClear: false,
                showCount: false,
                type: 'Day or Time'
            },
            {
                category: 'Time',
                title: 'Time',
                keywords: [],
                canClear: false,
                showCount: false,
                type: 'Day or Time'
            },
            {
                category: 'Availability',
                title: 'Availability',
                keywords: [],
                canClear: false,
                showCount: false,
                type: 'Type'
            },
            {
                category: 'Accessibility',
                title: 'Accessibility',
                keywords: [],
                canClear: false,
                showCount: false,
                type: 'Type'
            },
            {
                category: 'Theatre-Plus',
                title: 'Theatre Plus',
                keywords: [],
                canClear: false,
                showCount: false,
                type: 'Type'
            },
            {
                category: 'Production',
                title: 'Production',
                keywords: [],
                canClear: false,
                showCount: false,
                type: 'Type'
            },
            {
                category: 'Month',
                title: 'Month',
                keywords: [],
                canClear: false,
                showCount: false,
                type: 'Day or Time'
            },
            {
                category: 'Location',
                title: 'Location',
                keywords: [],
                canClear: false,
                showCount: false,
                type: 'Type'
            },
        ];

        // All facets belonging to all items
        var allFacets = items.reduce((carry, item) => {
            return [].concat(carry, item.facets);
        }, []);

        // Generate filters
        return filters.reduce((carry, filter) => {
            // Grab available keywords for filter
            var keywords = allFacets.filter(item => {
                return item.category === filter.category;
            });

            // Sort :3
            keywords = orderBy(keywords, item => {
                if ('sort' in item) {
                    return item.sort;
                }

                return item.keyword;
            });

            // Group for count ;)
            keywords = groupBy(keywords, 'keyword');

            // Assign keywords
            filter.keywords = Object.keys(keywords).map(item => {
                return {
                    title: item,
                    disabled: false,
                    checked: false,
                    visible: true,
                    count: keywords[item].length
                };
            });

            if (filter.keywords.length) {
                carry.push(filter);
            }

            return carry;
        }, []);
    }

    function applyFilters(params) {
        // Start off with current all items (non filtered)
        var filteredItems = params.items;

        // To keep track of applied filters
        var appliedFacets = [];

        // Loop through filters
        params.filters.forEach(filter => {
            // Grab selected keywords
            var selectedFacets = filter.keywords.filter(item => {
                return item.checked && !item.disabled;
            });

            // Skip if this filter not being used
            if (!selectedFacets.length) {
                return;
            }

            // Grab a nice set that can be used to compare
            selectedFacets = selectedFacets.map(item => {
                return {
                    keyword: item.title,
                    category: filter.category
                };
            });

            // Keep track of applied facet
            appliedFacets = [].concat(appliedFacets, selectedFacets);

            // Apply facets to item set
            filteredItems = filteredItems.filter(item => {
                // Match filtered facets with item facets
                return applyFacets(item.facets, selectedFacets).length;
            });
        });

        return {
            items: filteredItems,
            facets: appliedFacets
        };
    }

    function updateFilters(params) {
        // Grab a list of applied filters
        const appliedFacets = params.facets.map(filter => {
            return filter.keyword;
        });

        // Begin with full list
        let filteredItems = params.items;

        // Re-work filters
        return params.filters.map(filter => {
            // Can filter be cleared?
            filter.canClear = appliedFacets.indexOf(filter.category) !== -1;

            // Re-work facets
            filter.keywords = filter.keywords.map(keyword => {
                // No need to process if already checked
                if (keyword.checked) {
                    return keyword;
                }

                // Selected facets + current facet
                let potentialFacets = [].concat(params.facets, {
                    keyword: keyword.title,
                    category: filter.category
                });

                // Group by name for ease of comparision
                potentialFacets = groupBy(potentialFacets, 'category');

                // Start off with (potential) keyword filtered items
                let potentialResults = filteredItems;

                // Apply filters
                forEach(potentialFacets, facets => {
                    // Potential results IF current facet is applied
                    potentialResults = potentialResults.filter(item => {
                        return applyFacets(item.facets, facets).length;
                    });
                });

                // Grab facets off results
                potentialFacets = potentialResults.reduce((carry, item) => {
                    return [].concat(carry, item.facets);
                }, []);

                // But we only want the current facet
                potentialFacets = potentialFacets.filter(item => {
                    return (
                        item.category === filter.category &&
                        item.keyword === keyword.title
                    );
                });

                // Update to the "would-be" total
                keyword.count = potentialFacets.length;

                // Will the future state mean empty results?
                keyword.disabled = !keyword.count;

                // Return!
                return keyword;
            });

            // Return!
            return filter;
        });
    }

    function applyFacets(itemFacets, facets) {
        return intersectionWith(itemFacets, facets, (a, b) => {
            return (
                a.category === b.category &&
                a.keyword === b.keyword
            );
        });
    }

    function utmParams() {
        if (utmParamCache === null) {
            utmParamCache = pickBy($location.search(), (value, key) => {
                return key.indexOf('utm_') === 0;
            });
        }

        return utmParamCache;
    }

    function locationFacets(filters, returnObject) {
        // Facets in string?
        var locationQuery = $location.search();

        if (!locationQuery || !Object.keys(locationQuery).length) {
            if (!returnObject) {
                return false;
            }

            locationQuery = {};
        }

        // Keyword
        var keyword = '';

        if ('keyword' in locationQuery) {
            keyword = locationQuery['keyword'];
        }

        var range = {
            start: null,
            end: null
        };

        if ('start' in locationQuery) {
            range.start = moment(locationQuery['start']);
        }

        if ('end' in locationQuery) {
            range.end = moment(locationQuery['end']);
        }

        // Filters
        var selectedFilters = filters.map(function (filter) {
            // Grab selected facets for current filter
            var selected = [];

            if (filter.category in locationQuery) {
                selected = locationQuery[filter.category];
            }

            filter.facets = filter.facets.map(function (facet) {
                // Update checked flag
                facet.checked = selected.indexOf(facet.keyword) !== -1;

                // Return!
                return facet;
            });

            // Return!
            return filter;
        });

        // Return!
        return {
            keyword: keyword,
            filters: selectedFilters,
            range: range
        };
    }

    function updateLocation(params) {
        // Keep track of params
        var query = {};

        // Keyword is awkward...
        if (isString(params.keyword) && params.keyword.length) {
            query['keyword'] = params.keyword;
        }

        // Rnage is also...
        if (params.range.start) {
            query['start'] = params.range.start;
        }

        // ...awkward
        if (params.range.start) {
            query['end'] = params.range.end;
        }

        // Group for easy management
        var facets = groupBy(params.facets, 'category');

        // Loop and add to param list
        forEach(facets, function (items, category) {
            // Grab facets from filter and add to query
            query[category] = items.map(function (item) {
                return item.keyword;
            });
        });

        // Update URL
        $location.search(query);
    }

    function groupByMonth(items) {
        let groupedItems = groupBy(items, item => {
            return item.start_date_moment.clone().endOf('month').format();
        });

        groupedItems = map(groupedItems, (group, key) => {
            return {
                title: dates.moment(key).format('MMMM YYYY'),
                sort: key,
                items: group
            };
        });

        // Also sort :3
        return orderBy(groupedItems, 'sort');
    }

    function groupByDay(items) {
        let yearCache = [];

        let groupedItems = groupBy(items,  item => {
            return item.start_date_moment
               .clone()
               .startOf('day')
               .add(12, 'hours')
               .format();
        });

        return map(groupedItems, function (group, key) {
            const date = dates.moment(key);

            let year = null;
            let groupYear = date.format('YYYY');

            if ( yearCache.indexOf(groupYear) == -1 ) {
                year = groupYear;
                yearCache.push(groupYear);
            }

            return {
                label: $sce.trustAsHtml(groupLabelDay(key)),
                day: date.format('dddd'),
                month: date.format('MMMM'),
                dayOfMonth: date.format('D'),
                year: year,
                items: group
            }
        });
    }

    function groupByYear(items) {
        let groupedItems = groupBy(items, item => {
            return item.start_date_moment.clone().endOf('year').format();
        });

        return map(groupedItems, function (group, key) {
            return {
                label: groupLabelYear(key),
                items: group
            }
        });
    }

    function groupLabelDay(date) {
        var dt = dates.moment(date);

        return dt.format('dddd, <b>MMMM D</b>');
    }

    function groupLabelYear(date) {
        var dt = dates.moment(date);

        return dt.format('YYYY');
    }

    function checkPromoCode(code) {
        return inventoryService.checkPromoCode(code);
    }

    function removePromoCode() {
        return inventoryService.removePromoCode();
    }

    function addPromoCode(code) {
        return inventoryService.addPromoCode(code);
    }

    return {
        fuseOptions: fuseOptions,
        getPerformances: getPerformances,
        getPerformancesByPrices: getPerformancesByPrices,
        getPerformancesBySeason: getPerformancesBySeason,
        generateFacets: generateFacets,
        applyFilters: applyFilters,
        updateFilters: updateFilters,
        locationFacets: locationFacets,
        updateLocation: updateLocation,
        groupByMonth: groupByMonth,
        groupByDay: groupByDay,
        groupByYear: groupByYear,
        checkPromoCode: checkPromoCode,
        removePromoCode: removePromoCode,
        addPromoCode: addPromoCode,
    };
}];



// WEBPACK FOOTER //
// ./widgets/services/plannerService.js